import { Sprite, type Texture } from "pixi.js";
import {
  DirectionalAnimator,
  type DirectionalAnimatorOptions,
  type Dir8,
} from "./DirectionalAnimator";

export type AnimState = string;

export interface AnimClip {
  /** Per-direction frame arrays. */
  framesByDir: Partial<Record<Dir8, Texture[]>>;
  fps?: number;
  loop?: boolean;
  /** When the clip finishes (loop=false), automatically transition here. */
  next?: AnimState;
}

export interface AnimationStateMachineOptions extends DirectionalAnimatorOptions {
  initial?: AnimState;
}

/**
 * Layered on top of DirectionalAnimator: maps named states ("idle", "walk",
 * "attack", "hurt", "death", …) to clips, with optional auto-transitions when
 * a non-loop clip finishes.
 *
 *   const sm = new AnimationStateMachine({
 *     idle: { framesByDir: idleByDir, fps: 4 },
 *     walk: { framesByDir: walkByDir, fps: 8 },
 *     attack: { framesByDir: attackByDir, fps: 12, loop: false, next: "idle" },
 *   }, { initial: "idle" });
 *   sm.attach(sprite);
 *   sm.setHeading(entity.heading);
 *   sm.transition("walk");
 *   game.addUpdateSystem({ update: (dt) => sm.update(dt, sprite) });
 */
export class AnimationStateMachine {
  private clips: Record<AnimState, AnimClip>;
  private state: AnimState;
  private animator: DirectionalAnimator;
  private resolution: "4" | "8";
  private heading = 0;
  private finished = false;
  private listeners = new Set<(state: AnimState) => void>();

  constructor(clips: Record<AnimState, AnimClip>, opts: AnimationStateMachineOptions = {}) {
    if (Object.keys(clips).length === 0) throw new Error("AnimationStateMachine needs at least one clip");
    this.clips = clips;
    this.resolution = opts.resolution ?? "4";
    this.state = opts.initial ?? Object.keys(clips)[0]!;
    const clip = this.clips[this.state]!;
    this.animator = new DirectionalAnimator(clip.framesByDir, {
      fps: clip.fps ?? opts.fps ?? 8,
      loop: clip.loop ?? opts.loop ?? true,
      resolution: this.resolution,
    });
  }

  attach(sprite: Sprite): void {
    this.animator.attach(sprite);
  }

  setHeading(heading: number): void {
    this.heading = heading;
    this.animator.setHeading(heading);
  }

  /** Switch state. No-op if already in `state`. Resets frame index. */
  transition(state: AnimState): void {
    if (state === this.state) return;
    const clip = this.clips[state];
    if (!clip) throw new Error(`unknown animation state "${state}"`);
    this.state = state;
    this.finished = false;
    this.nonLoopElapsed = 0;
    this.animator.setFrames(clip.framesByDir, {
      ...(clip.fps !== undefined ? { fps: clip.fps } : {}),
      ...(clip.loop !== undefined ? { loop: clip.loop } : {}),
      resolution: this.resolution,
    });
    this.animator.setHeading(this.heading);
    this.animator.reset();
    for (const fn of this.listeners) fn(state);
  }

  current(): AnimState {
    return this.state;
  }

  /** Emit when state changes. */
  onChange(fn: (state: AnimState) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  update(dt: number, sprite?: Sprite): void {
    this.animator.update(dt, sprite);
    const clip = this.clips[this.state];
    if (!clip || clip.loop !== false || this.finished) return;
    this.nonLoopElapsed += dt;
    if (this.nonLoopElapsed < this.estimateClipDuration(clip)) return;
    this.finished = true;
    if (clip.next) this.transition(clip.next);
  }

  /** Whether a non-loop clip has reached its last frame. */
  isFinished(): boolean {
    return this.finished;
  }

  private estimateClipDuration(clip: AnimClip): number {
    const fps = clip.fps ?? 8;
    const someDir = Object.values(clip.framesByDir).find((f) => f && f.length > 0);
    return ((someDir?.length ?? 1) - 1) / fps;
  }

  private nonLoopElapsed = 0;
}
